import { Input, Checkbox, Select, Tooltip } from "antd";
import { Plus, Sparkles, X } from "lucide-react";
import { nanoid } from "nanoid";
import { useState } from "react";
import {
  EMPLOYMENT_TYPE_OPTIONS,
  RESUME_SECTION_TYPES,
} from "../../../optimizeResume/editor/config";
import SectionCardShell from "../../../optimizeResume/editor/SectionCardShell";
import WriteWithAiBulletsModal from "../../modals/WriteWithAiBulletsModal";

const { TextArea } = Input;

const blankItem = () => ({
  id: `exp-${nanoid(6)}`,
  company: "",
  role: "",
  employmentType: "Full-time",
  location: "",
  startDate: "",
  endDate: "",
  current: false,
  // domain: "",
  // techStack: [],
  responsibilities: [],
});

export default function Experience({
  id,
  data,
  onChange,
  onDelete,
  allCollapsed,
}) {
  const items = data ?? [];
  const [aiBulletsIdx, setAiBulletsIdx] = useState(null);

  const updateItem = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange(next);
  };

  const removeItem = (idx) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  const addItem = () => {
    onChange([...items, blankItem()]);
  };

  const updateBullet = (idx, bIdx, val) => {
    const next = items.map((it, i) => {
      if (i !== idx) return it;

      const responsibilities = (it.responsibilities ?? []).map((b, j) =>
        j === bIdx ? val : b,
      );

      return {
        ...it,
        responsibilities,
      };
    });

    onChange(next);
  };

  const addBullet = (idx) => {
    const next = items.map((it, i) =>
      i === idx
        ? {
            ...it,
            responsibilities: [...(it.responsibilities ?? []), ""],
          }
        : it,
    );

    onChange(next);
  };

  const removeBullet = (idx, bIdx) => {
    const next = items.map((it, i) => {
      if (i !== idx) return it;

      return {
        ...it,
        responsibilities: (it.responsibilities ?? []).filter(
          (_, j) => j !== bIdx,
        ),
      };
    });

    onChange(next);
  };

  return (
    <SectionCardShell
      id={id}
      type={RESUME_SECTION_TYPES.EXPERIENCE}
      data={data}
      onChange={onChange}
      onDelete={onDelete}
      allCollapsed={allCollapsed}
    >
      <div className="flex flex-col gap-4">
        {items.map((it, idx) => (
          <div
            key={it.id}
            className="border border-gray-200 rounded-lg p-3 bg-gray-50/40"
          >
            <div className="flex justify-between items-start mb-2">
              <p className="font-medium text-sm text-[#344054]">
                {it.role || it.company || `Experience #${idx + 1}`}
              </p>
              <button
                onClick={() => removeItem(idx)}
                className="text-gray-400 hover:text-red-600"
                aria-label="Remove experience"
              >
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Company / Firm"
                value={it.company}
                onChange={(e) => updateItem(idx, { company: e.target.value })}
              />
              <Input
                placeholder="Role / Title"
                value={it.role}
                onChange={(e) => updateItem(idx, { role: e.target.value })}
              />
              <Select
                value={it.employmentType}
                onChange={(val) => updateItem(idx, { employmentType: val })}
                options={EMPLOYMENT_TYPE_OPTIONS.map((v) => ({
                  value: v,
                  label: v,
                }))}
                placeholder="Employment type"
              />
              <Input
                placeholder="Location"
                value={it.location}
                onChange={(e) => updateItem(idx, { location: e.target.value })}
              />
              <Input
                placeholder="Start (YYYY-MM)"
                value={it.startDate}
                onChange={(e) => updateItem(idx, { startDate: e.target.value })}
              />
              <Input
                placeholder="End (YYYY-MM)"
                value={it.endDate}
                disabled={it.current}
                onChange={(e) => updateItem(idx, { endDate: e.target.value })}
              />
              <div className="col-span-2 flex items-center gap-2">
                <Checkbox
                  checked={it.current}
                  onChange={(e) =>
                    updateItem(idx, {
                      current: e.target.checked,
                      endDate: e.target.checked ? "" : it.endDate,
                    })
                  }
                  className="text-[#667085]"
                >
                  Currently working here
                </Checkbox>
              </div>
              {/* <Input
                placeholder="Domain / Industry (e.g. Audit, Healthtech, Tax)"
                value={it.domain}
                onChange={(e) => updateItem(idx, { domain: e.target.value })}
              />
              <Select
                mode="tags"
                value={it.techStack ?? []}
                onChange={(vals) => updateItem(idx, { techStack: vals })}
                placeholder="Tech stack / Tools (e.g. Tally, SAP, Java)"
                tokenSeparators={[","]}
                style={{ width: "100%" }}
              /> */}
            </div>

            <div className="mt-3 border border-dashed border-[#FEDF89] rounded-lg p-3 bg-[#FFFAEB]/40">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-[#667085] mb-0">
                  Bullets
                </p>
                <Tooltip
                  overlayInnerStyle={{
                    borderRadius: "6px",
                    padding: "6px 10px",
                    backgroundColor: "#0C111D",
                    color: "#fff",
                  }}
                  title="Generate bullets with AI"
                >
                  <button
                    type="button"
                    onClick={() => setAiBulletsIdx(idx)}
                    className="flex items-center gap-2 text-[.75rem] font-medium text-[#DC6803] hover:text-amber-500 border border-[#FEDF89] bg-[#FFFAEB] rounded-full px-2 py-[.2rem] transition-all duration-200"
                  >
                    {/* <svg
                      width="12"
                      height="12"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3.70313 5.69922H3.70979M6.70313 5.69922H6.70979M9.70313 5.69922H9.70979M3.36979 10.6992V12.2562C3.36979 12.6115 3.36979 12.7891 3.44261 12.8803C3.50594 12.9596 3.60197 13.0058 3.70348 13.0057C3.82021 13.0056 3.95891 12.8946 4.2363 12.6727L5.8266 11.4004C6.15147 11.1405 6.3139 11.0106 6.49478 10.9182C6.65526 10.8362 6.82608 10.7763 7.0026 10.7401C7.20157 10.6992 7.40959 10.6992 7.82562 10.6992H9.50313C10.6232 10.6992 11.1833 10.6992 11.6111 10.4812C11.9874 10.2895 12.2934 9.98352 12.4851 9.6072C12.7031 9.17938 12.7031 8.61932 12.7031 7.49922V3.89922C12.7031 2.77911 12.7031 2.21906 12.4851 1.79124C12.2934 1.41491 11.9874 1.10895 11.6111 0.917206C11.1833 0.699219 10.6232 0.699219 9.50313 0.699219H3.90313C2.78302 0.699219 2.22297 0.699219 1.79514 0.917206C1.41882 1.10895 1.11286 1.41491 0.921112 1.79124C0.703125 2.21906 0.703125 2.77911 0.703125 3.89922V8.03255C0.703125 8.65253 0.703125 8.96252 0.771273 9.21686C0.956208 9.90704 1.4953 10.4461 2.18549 10.6311C2.43982 10.6992 2.74981 10.6992 3.36979 10.6992ZM4.03646 5.69922C4.03646 5.88331 3.88722 6.03255 3.70313 6.03255C3.51903 6.03255 3.36979 5.88331 3.36979 5.69922C3.36979 5.51512 3.51903 5.36589 3.70313 5.36589C3.88722 5.36589 4.03646 5.51512 4.03646 5.69922ZM7.03646 5.69922C7.03646 5.88331 6.88722 6.03255 6.70313 6.03255C6.51903 6.03255 6.36979 5.88331 6.36979 5.69922C6.36979 5.51512 6.51903 5.36589 6.70313 5.36589C6.88722 5.36589 7.03646 5.51512 7.03646 5.69922ZM10.0365 5.69922C10.0365 5.88331 9.88722 6.03255 9.70313 6.03255C9.51903 6.03255 9.36979 5.88331 9.36979 5.69922C9.36979 5.51512 9.51903 5.36589 9.70313 5.36589C9.88722 5.36589 10.0365 5.51512 10.0365 5.69922Z"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg> */}
                    <Sparkles size={14.5} className="" />
                    Write with AI
                  </button>
                </Tooltip>
              </div>
              <div className="flex flex-col gap-2">
                {(it.responsibilities ?? []).map((b, bIdx) => (
                  <div key={bIdx} className="flex gap-2 items-start">
                    <TextArea
                      value={b}
                      autoSize={{ minRows: 1, maxRows: 4 }}
                      onChange={(e) => updateBullet(idx, bIdx, e.target.value)}
                      placeholder="Describe an achievement…"
                    />
                    <button
                      onClick={() => removeBullet(idx, bIdx)}
                      className="text-gray-400 hover:text-red-600 mt-2"
                      aria-label="Remove bullet"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addBullet(idx)}
                  className="self-start flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                >
                  <Plus size={14} /> Add bullet
                </button>
              </div>
            </div>
          </div>
        ))}
        <button
          onClick={addItem}
          className="self-start flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
        >
          <Plus size={14} /> Add experience
        </button>
      </div>

      <WriteWithAiBulletsModal
        open={aiBulletsIdx !== null}
        setOpen={(v) => setAiBulletsIdx(v ? aiBulletsIdx : null)}
        item={aiBulletsIdx !== null ? items[aiBulletsIdx] : null}
        bulletsField="responsibilities"
        sectionKey="experiences"
        infoContent={
          aiBulletsIdx !== null
            ? (() => {
                const it = items[aiBulletsIdx] ?? {};
                const start = it.startDate || "";
                const end = it.current ? "Present" : it.endDate || "";
                const dateRange =
                  start && end ? `${start} – ${end}` : start || end;
                return (
                  <div className="flex flex-col gap-[.15rem]">
                    <p className="text-[.9rem] font-semibold text-[#0F1B23] mb-0">
                      {it.role || "—"}
                    </p>
                    <p className="text-[.8rem] text-[#475467] mb-0">
                      {it.company || "—"}
                      {it.employmentType ? ` · ${it.employmentType}` : ""}
                    </p>
                    {(it.location || dateRange) && (
                      <p className="text-[.75rem] text-[#667085] mb-0">
                        {it.location || ""}
                        {it.location && dateRange ? " · " : ""}
                        {dateRange}
                      </p>
                    )}
                  </div>
                );
              })()
            : null
        }
        onApply={(bullets) => {
          if (aiBulletsIdx !== null) {
            updateItem(aiBulletsIdx, { responsibilities: bullets });
          }
        }}
      />
    </SectionCardShell>
  );
}
